import { SettingsApplications } from '@mui/icons-material';
import { Alert, Button, MenuItem } from '@mui/material';
import _ from 'lodash';
import { useTranslation } from 'next-i18next';
import React, { useEffect, useState } from 'react';
import { MdKeyboardArrowRight, MdLink } from 'react-icons/md';
import toast from 'react-hot-toast';
import { Meetingroom } from '../../../../../../interfaces/interfaces';
import { useSelector } from '../../../../../../reduxToolkit/typedRedux';
import { getStudios } from '../../../../../../services/services';
import { MeetingroomQualityIcon } from '../../../../../common/MeetingroomQualityIcon';
import VyButton from '../../../../../common/VyButton';
import VyModal from '../../../../../common/VyModal';
import VySelect from '../../../../../common/VySelect';
import VyTooltip from '../../../../../common/VyTooltip';
import { StageSettingsTableInterface } from '../StageSettingsTable';
import { StudioConfiguration } from './StudioConfiguration';
import { CopyToClipboard } from '../../../../../common/CopyToClipboard';

export const StudioSelect = ({ component, onChange }: StageSettingsTableInterface) => {
  const { t } = useTranslation();

  const event = useSelector(({ common }) => common.event);

  const [studios, setStudios] = useState<Meetingroom[]>([]);
  const [currentStudio, setCurrentStudio] = useState<Meetingroom | undefined>();
  const [showStudioModal, setShowStudioModal] = useState(false);
  const [loading, setLoading] = useState(false);

  const joinLink = `${window.origin}/videochat/${currentStudio?._id}`;

  async function fetchStudios(newStudio?: Meetingroom) {
    setLoading(true);

    try {
      const data = await getStudios(event!._id);
      setStudios(data);

      if (newStudio) {
        setCurrentStudio(newStudio);
      } else {
        setCurrentStudio(data.find((i) => i._id === component?.stageSettings?.vystemStudio?._id));
      }
    } catch (err) {
      console.error(err);
    }
    setLoading(false);
  }

  async function handleJoin() {
    setLoading(true);
    try {
      window?.open(joinLink);
    } catch (err) {
      console.error(err);
    }
    setLoading(false);
  }

  useEffect(() => {
    fetchStudios();
  }, [component]);

  return (
    <>
      <p>{t('account:studio.subtitle')}</p>
      {studios?.length > 0 ? (
        <div className="flex flex-row space-x-2 w-full">
          <VySelect
            variant="outlined"
            fullWidth
            value={_.get(component, 'stageSettings.vystemStudio._id')}
            onChange={(e: any) => {
              const _component = _.cloneDeep(component);
              _.set(
                _component,
                'stageSettings.vystemStudio',
                studios?.find((i) => i._id === e.target.value)
              );
              onChange(_component);
            }}
            MenuProps={{
              style: { zIndex: 1320 },
            }}>
            {studios?.map((studio) => (
              <MenuItem value={studio._id}>
                {studio.name} <MeetingroomQualityIcon meeting={studio} />
              </MenuItem>
            ))}
          </VySelect>
          {currentStudio && (
            <VyTooltip title={t(currentStudio ? 'pagebuilder:stage-open-studio-modal' : 'pagebuilder:stage-open-studio-disabled')}>
              <Button disabled={!currentStudio} variant="outlined" className="h-full" onClick={() => setShowStudioModal(true)}>
                <SettingsApplications />
              </Button>
            </VyTooltip>
          )}
        </div>
      ) : (
        <div className="space-y-2 w-full">
          <Alert severity="info">{t('pagebuilder:no-studio-info')}</Alert>
        </div>
      )}
      <VyModal open={showStudioModal} onClose={() => setShowStudioModal(false)}>
        {(onClose) => (
          <StudioConfiguration
            onClose={onClose}
            onUpdate={(studio) => {
              const _component = _.cloneDeep(component);
              _.set(_component, 'stageSettings.vystemStudio', studio);
              onChange(_component);
            }}
            studio={currentStudio!}
          />
        )}
      </VyModal>
      {currentStudio && (
        <div className="flex my-2 space-x-2">
          <VyButton className="flex justify-center items-center w-full" onClick={handleJoin} showLoadingIcon={loading} disabled={loading}>
            {t('common:join')} <MdKeyboardArrowRight />
          </VyButton>
          <div className="w-full">
            <CopyToClipboard text={`${joinLink}?pw=${currentStudio.studioPassword}`} onCopy={() => toast.success(t('account:copy-success'))}>
              <VyTooltip title={t('pagebuilder:copy-studio-link-tooltip')}>
                <VyButton className="flex justify-center items-center w-full">{t('pagebuilder:copy-studio-link')}</VyButton>
              </VyTooltip>
            </CopyToClipboard>
          </div>
        </div>
      )}
    </>
  );
};
